import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAuth, StsConfigLoader } from 'angular-auth-oidc-client';

import { routesConfig } from '@config/routes.config';
import { ApigeeInterceptor } from '@interceptors/apigee.interceptor';
import { AuthService } from '@services/auth.service';
import { CustomErrorHandler } from '@services/custom-error-handler.service';
import { FullStoryService } from '@services/fullstory.service';
import { LaunchDarklyService } from '@services/launch-darkly.service';
import { NewRelicService } from '@services/new-relic.service';
import { SplunkLoggerService } from '@services/splunk-logger.service';

export const applicationConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(async () => {
      const fullstoryService = inject(FullStoryService);
      const launchDarklyService = inject(LaunchDarklyService);
      const newRelicService = inject(NewRelicService);
      const splunkLoggerService = inject(SplunkLoggerService);

      splunkLoggerService.info('Application starting');

      try {
        newRelicService.initialize();
        fullstoryService.initialize();
        await launchDarklyService.initialize();
        splunkLoggerService.info('Application loaded');
      } catch (error: unknown) {
        splunkLoggerService.error(error, { message: 'Application initialization failed' });
        // Force application to fail for initialization errors
        throw error;
      }
    }),
    provideAuth({
      loader: {
        provide: StsConfigLoader,
        useFactory: () => {
          const authService = inject(AuthService);
          return authService.createStsConfigLoader();
        },
      },
    }),
    provideBrowserGlobalErrorListeners(),
    provideHttpClient(withInterceptors([ApigeeInterceptor])),
    provideRouter(routesConfig),
    provideZonelessChangeDetection(),
    { provide: ErrorHandler, useClass: CustomErrorHandler },
  ],
};
